<script setup lang="ts">
import type { RangeFilterActionsProps } from '.';

import { cn } from '@/utils';
import { useLogger } from '~/composables/useLogger';

import { buttonVariants } from '~/components/ui/button';

import DoneIcon from '~icons/material-symbols/done';

const props = withDefaults(defineProps<RangeFilterActionsProps>(), {
	variant: 'primary',
	closeOnApply: false
});

const emit = defineEmits<{
	apply: [focusNext?: boolean];
	clear: [];
	'focus-previous': [];
}>();

const logger = useLogger('SrpRangeFilterActions');
const clearButtonRef = useTemplateRef<HTMLButtonElement>('clearButtonRef');
const applyButtonRef = useTemplateRef<HTMLButtonElement>('applyButtonRef');

const clearButtonClasses = computed(() =>
	cn(
		buttonVariants({ variant: 'transparent', size: 'sm' }),
		'h-11 flex-1 border-transparent bg-transparent px-0 disabled:border-transparent disabled:bg-transparent',
		props.variant === 'dark' && 'text-white hover:text-white'
	)
);

const applyButtonClasses = computed(() =>
	cn(
		buttonVariants({
			variant: props.variant === 'dark' ? 'primary-dark' : 'primary',
			size: 'sm'
		}),
		'h-11 flex-1'
	)
);

function logAction(action: 'apply' | 'clear') {
	logger.info('SRP range filter action', {
		action,
		filter: props.filterName
	});
}

function handleApply(focusNext = false) {
	logAction('apply');
	if (focusNext) {
		emit('apply', true);
		return;
	}

	emit('apply');
}

function handleApplyTab(event: KeyboardEvent) {
	if (!props.closeOnApply) return;

	event.preventDefault();
	event.stopPropagation();
	handleApply(true);
}

async function handleClear() {
	if (!props.hasValue) return;

	logAction('clear');
	emit('clear');
	await nextTick();
	applyButtonRef.value?.focus();
}

function focusFirstAction() {
	nextTick(() => {
		if (props.hasValue) {
			clearButtonRef.value?.focus();
			return;
		}

		applyButtonRef.value?.focus();
	});
}

function focusApplyButton() {
	applyButtonRef.value?.focus();
}

function focusPreviousFromApply() {
	if (props.hasValue) {
		clearButtonRef.value?.focus();
		return;
	}

	emit('focus-previous');
}

defineExpose({ focusFirstAction });
</script>

<template>
	<footer
		class="flex w-full items-center justify-between gap-4 border-t border-white-dark-3 pt-4"
		:class="{ 'border-primary-light-3': props.variant === 'dark' }"
	>
		<button
			ref="clearButtonRef"
			type="button"
			:data-testid="`${props.filterName}-filter-clear-button`"
			:class="clearButtonClasses"
			:disabled="!props.hasValue"
			@mousedown.prevent
			@click.stop="handleClear"
			@keydown.enter.prevent.stop="handleClear"
			@keydown.tab.exact.prevent.stop="focusApplyButton"
			@keydown.shift.tab.prevent.stop="$emit('focus-previous')"
		>
			Limpar
		</button>

		<button
			ref="applyButtonRef"
			type="button"
			:data-testid="`${props.filterName}-filter-apply-button`"
			:class="applyButtonClasses"
			@mousedown.prevent
			@click.stop="handleApply()"
			@keydown.enter.prevent.stop="handleApply()"
			@keydown.tab.exact="handleApplyTab"
			@keydown.shift.tab.prevent.stop="focusPreviousFromApply"
		>
			<DoneIcon class="size-5" aria-hidden="true" />
			Aplicar
		</button>
	</footer>
</template>
